import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AudioTrack } from '../types';
import { useAudioTracks } from '../hooks';
import CategorySelector, { Category } from '../components/CategorySelector';
import TrackList from '../components/TrackList';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TrialBanner from '../components/TrialBanner';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';

const TracksPage: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation('tracks');
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { getSubscriptionStatus, subscription, isTrialActive } =
    useSubscription();
  const [selectedTag, setSelectedTag] = useState<string>('ALL');

  // Use the custom hook for audio tracks
  const { tracks: allTracks, loading, error, refetch } = useAudioTracks();

  // Derive unique tags from all tracks
  const tagCategories = React.useMemo((): Category[] => {
    const tagSet = new Set<string>();
    allTracks.forEach((track) => {
      (track.tags || []).forEach((tag) => tagSet.add(tag));
    });
    const allOption: Category = { id: 'ALL', name: t('tags.all') };
    const tagOptions: Category[] = Array.from(tagSet)
      .sort()
      .map((tag) => ({ id: tag, name: t(`tags.${tag}`, tag) }));
    return [allOption, ...tagOptions];
  }, [allTracks, t]);

  // Filter tracks based on selected tag
  const tracks = React.useMemo(() => {
    if (selectedTag === 'ALL' || selectedTag === '') {
      return allTracks;
    }
    return allTracks.filter((track) =>
      (track.tags || []).includes(selectedTag),
    );
  }, [allTracks, selectedTag]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      history.push('/login');
    }
  }, [authLoading, isAuthenticated, history]);

  const handleTrackSelect = (track: AudioTrack) => {
    history.push(`/player/${track.id}`, {
      track: {
        id: track.id,
        name: track.name,
        thumbnail: track.thumbnail,
        tags: track.tags,
      },
    });
  };

  const handleTagChange = (tagId: string) => {
    setSelectedTag(tagId);
  };

  const subscriptionStatus = getSubscriptionStatus();
  const trialEndDate =
    subscription?.type === 'TRIAL' ? subscription.currentPeriodEnd : undefined;

  return (
    <div className='page'>
      <Header variant='player' />

      <div className='container-mobile'>
        {subscription?.type === 'TRIAL' && isTrialActive() && (
          <TrialBanner subscription={subscription} />
        )}

        <h1 className='page-title'>{t('title')}</h1>

        <CategorySelector
          categories={tagCategories}
          selectedCategory={selectedTag}
          onCategoryChange={handleTagChange}
        />
        <div className='track-card-list-wrapper'>
          <TrackList
            tracks={tracks}
            onTrackSelect={handleTrackSelect}
            subscriptionStatus={subscriptionStatus}
            trialEndDate={trialEndDate}
            loading={loading}
            error={error}
            onRetry={refetch}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TracksPage;
